import React from 'react'
import {Text, Link} from '@chakra-ui/react'
import {defineMessages, FormattedMessage} from 'react-intl'
import {Box, List, ListItem} from '@salesforce/retail-react-app/app/components/shared/ui'
import {css} from '@emotion/react'
import PropTypes from 'prop-types'
import {areFieldsEmpty, areTraceabilityFieldsEmpty, getCountryName} from '../../utils/product-utils'
import {isValidNumberString} from '../../utils/utils'

/**
 * Displays product information related to environmental qualities and characteristics.
 *
 * This component renders details about a product's environmental impact, including
 * traceability, use of recycled materials, presence of microplastics, and hazardous substances.
 * It also provides a link to a PDF document with additional information if available.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} props.isVariant - Indicates if the product is a variant.
 * @param {boolean} props.isAgecProduct - Indicates if the product is an AGEC product.
 * @param {string} [props.madeIn] - The ISO3 country code where the product is made.
 * @param {string} [props.origineMatiere] - The ISO3 country code of the material origin.
 * @param {string} [props.transformation] - The ISO3 country code where the product is transformed.
 * @param {string} [props.pourcentageMatiereRecyclee] - The percentage of recycled material in the product.
 * @param {string} [props.contientMicroplastique] - Indicates if the product contains microplastics.
 * @param {string} [props.contientSubstanceDangereuse] - Indicates if the product contains hazardous substances.
 * @param {string} [props.agecPdfLocation] - The URL to the AGEC PDF document.
 * @returns {JSX.Element|null} - The rendered component or null if the product is not a variant or not an AGEC product.
 */
const ProductAGEC = ({
    isAgecProduct,
    madeIn,
    origineMatiere,
    transformation,
    pourcentageMatiereRecyclee,
    contientMicroplastique,
    contientSubstanceDangereuse,
    agecPdfLocation
}) => {
    if (!isAgecProduct) {
        return null
    }

    const pourcentageMatiereRecycleeValue = (() => {
        const val = pourcentageMatiereRecyclee?.replace(/[>=<% ]/g, '')
        if (isValidNumberString(val)) {
            return val
        }
        return ''
    })()

    /**
     * Checks if all traceability fields are empty.
     *
     * This uses the utility function `areTraceabilityFieldsEmpty` to determine if
     * the fields related to the product's traceability (madeIn, origineMatiere, transformation)
     * are all empty or undefined.
     *
     * @type {boolean}
     */
    const allTraceabilityFieldsEmpty = areTraceabilityFieldsEmpty(
        madeIn,
        origineMatiere,
        transformation
    )

    /**
     * Checks if all relevant product fields are empty.
     *
     * This uses the utility function `areFieldsEmpty` to determine if all the fields
     * related to the product's environmental impact and characteristics are empty or undefined.
     * This includes fields like madeIn, origineMatiere, transformation, pourcentageMatiereRecyclee,
     * contientMicroplastique, and contientSubstanceDangereuse.
     *
     * @type {boolean}
     */
    const allFieldsEmpty = areFieldsEmpty([
        madeIn,
        origineMatiere,
        transformation,
        pourcentageMatiereRecyclee,
        contientMicroplastique,
        contientSubstanceDangereuse
    ])

    // Define messages for translation
    const messages = defineMessages({
        traceability: {
            id: 'product_agec.pdp.traceability',
            defaultMessage: 'Made in'
        },
        manufacturing: {
            id: 'product_agec.pdp.manufacturing',
            defaultMessage: 'Manufacturing'
        },
        tailoringDyeing: {
            id: 'product_agec.pdp.tailoring_dyeing',
            defaultMessage: 'Dyeing - Printing'
        },
        weaving: {
            id: 'product_agec.pdp.weaving',
            defaultMessage: 'Weaving - Knitting'
        },
        recycledFibers: {
            id: 'product_agec.pdp.recycled_fibers',
            defaultMessage: 'Recycled Fibers'
        },
        recycledFiberUsage: {
            id: 'product_agec.pdp.recycled_fiber_usage',
            defaultMessage:
                'Use of recycled fiber: this product contains at least {percentage}% of recycled fibers'
        },
        microplastics: {
            id: 'product_agec.pdp.microplastics',
            defaultMessage: 'Microplastics'
        },
        microplasticsWarning: {
            id: 'product_agec.pdp.microplastics_warning',
            defaultMessage: 'This product releases microplastics during washing.'
        },
        dangerousSubstances: {
            id: 'product_agec.pdp.dangerous_substances',
            defaultMessage: 'Dangerous substances'
        },
        dangerousSubstancesWarning: {
            id: 'product_agec.pdp.dangerous_substances_warning',
            defaultMessage: 'This product contains hazardous chemicals.'
        },
        packagingGuide: {
            id: 'product_agec.pdp.packaging_guide',
            defaultMessage: 'Packaging guide'
        },
        packagingGuideLink: {
            id: 'product_agec.pdp.packaging_guide_link',
            defaultMessage: 'Click here to review our packaging guide.'
        },
        beingRevised: {
            id: 'product_agec.pdp.being_revised',
            defaultMessage:
                'The product page relating to environmental qualities and characteristics is currently being revised.'
        },
        shortTextBeingRevised: {
            id: 'product_agec.pdp.short_text_being_revised',
            defaultMessage: 'Being revised.'
        }
    })

    return (
        <>
            {isAgecProduct && (
                <Box ml={{base: '20px', lg: '30px'}} fontSize={'md'}>
                    {allTraceabilityFieldsEmpty || allFieldsEmpty ? (
                        <Text color={'darkGray'}>
                            <FormattedMessage {...messages.beingRevised} />
                        </Text>
                    ) : (
                        <>
                            <Box>
                                <Text role="heading" aria-level="3">
                                    <FormattedMessage {...messages.traceability} />
                                </Text>
                                <List mt={'10px'} color={'darkGray'} position="relative">
                                    <ListItem css={listItemStyle}>
                                        <FormattedMessage {...messages.manufacturing} />:
                                        <Box as="span" ml={2}>
                                            {getCountryName(madeIn)}
                                        </Box>
                                    </ListItem>
                                    <ListItem css={listItemStyle}>
                                        <FormattedMessage {...messages.weaving} />:
                                        <Box as="span" ml={2}>
                                            {getCountryName(origineMatiere)}
                                        </Box>
                                    </ListItem>
                                    <ListItem css={listItemStyle}>
                                        <FormattedMessage {...messages.tailoringDyeing} />:
                                        <Box as="span" ml={2}>
                                            {getCountryName(transformation)}
                                        </Box>
                                    </ListItem>
                                </List>
                            </Box>

                            <Box mt={'10px'}>
                                {(pourcentageMatiereRecyclee === undefined ||
                                    pourcentageMatiereRecycleeValue) && (
                                    // value is undefined display the recycled fibers subheader OR If value is defined and valid number
                                    <Text role="heading" aria-level="3">
                                        <FormattedMessage {...messages.recycledFibers} />
                                    </Text>
                                )}
                                {pourcentageMatiereRecyclee !== undefined && // If value is defined and valid number then display percentage
                                    pourcentageMatiereRecycleeValue && (
                                        <Text color={'darkGray'} mt={'10px'}>
                                            <FormattedMessage
                                                {...messages.recycledFiberUsage}
                                                values={{
                                                    percentage: pourcentageMatiereRecycleeValue
                                                }}
                                            />
                                        </Text>
                                    )}
                                {pourcentageMatiereRecyclee === undefined && ( // Display being revised message if value is undefined
                                    <Text color={'darkGray'} mt={'10px'}>
                                        <FormattedMessage {...messages.shortTextBeingRevised} />
                                    </Text>
                                )}
                            </Box>

                            {contientMicroplastique?.toLowerCase() === 'yes' && (
                                <Box mt={'10px'}>
                                    <Text role="heading" aria-level="3">
                                        <FormattedMessage {...messages.microplastics} />
                                    </Text>
                                    <Text color={'darkGray'} mt={'10px'}>
                                        <FormattedMessage {...messages.microplasticsWarning} />
                                    </Text>
                                </Box>
                            )}

                            {contientSubstanceDangereuse?.toLowerCase() === 'yes' && (
                                <Box mt={'10px'}>
                                    <Text role="heading" aria-level="3">
                                        <FormattedMessage {...messages.dangerousSubstances} />
                                    </Text>
                                    <Text color={'darkGray'} mt={'10px'}>
                                        <FormattedMessage
                                            {...messages.dangerousSubstancesWarning}
                                        />
                                    </Text>
                                </Box>
                            )}

                            <Box mt={'10px'}>
                                <Text role="heading" aria-level="3" mb={'10px'}>
                                    <FormattedMessage {...messages.packagingGuide} />
                                </Text>
                                <Link
                                    href={agecPdfLocation}
                                    isExternal
                                    textDecoration={'underline'}
                                    color={'darkGray'}
                                >
                                    <FormattedMessage {...messages.packagingGuideLink} />
                                </Link>
                            </Box>
                        </>
                    )}
                </Box>
            )}
        </>
    )
}

ProductAGEC.propTypes = {
    isAgecProduct: PropTypes.bool,
    madeIn: PropTypes.string,
    origineMatiere: PropTypes.string,
    transformation: PropTypes.string,
    pourcentageMatiereRecyclee: PropTypes.string,
    contientMicroplastique: PropTypes.string,
    contientSubstanceDangereuse: PropTypes.string,
    agecPdfLocation: PropTypes.string
}

const listItemStyle = css`
    position: relative;
    padding-left: 10px;

    &::before {
        content: '';
        position: absolute;
        top: 5px;
        left: 0;
        width: 0;
        height: 0;
        border-top: 3px solid transparent;
        border-bottom: 3px solid transparent;
        border-left: 4px solid var(--chakra-colors-darkGray);
    }
`

export default ProductAGEC
